<template>
  <validation-observer v-slot="{ handleSubmit }" slim>
    <form @submit.prevent="handleSubmit(submit)">
      <modal background>
        <div class="modal-card" ref="modal">
          <header class="modal-card-head">
            <p class="modal-card-title">
              {{ message ? "Edycja ogłoszenia" : "Nowe ogłoszenie" }}
            </p>
            <button
              type="button"
              class="delete"
              aria-label="close"
              @click="cancel"
            ></button>
          </header>

          <section class="modal-card-body">
            <div class="field">
              <label class="label">Tytuł ogłoszenia</label>
              <control-input
                type="text"
                placeholder="Wprowadź tytuł ogłoszenia..."
                rules="required"
                icon-left="fa-tag"
                v-model.trim="title"
              />
            </div>
            <div class="field">
              <label class="label">Treść ogłoszenia</label>
              <control-editor
                menu
                frame
                bold
                italic
                underline
                strike
                bullet-list
                ordered-list
                blockquote
                link
                line
                table
                :heading="[1, 2, 3]"
                history
                trailing-paragraph
                placeholder="Treść ogłoszenia..."
                v-model="content"
                rules="required"
              />
            </div>
            <div class="field">
              <label class="label">Data publikacji</label>
              <control-date-time-picker
                ref="date"
                v-model="date"
                :dates="[isDateAvailable]"
                rules="required"
                icon-left="fa-calendar-alt"
                placeholder="Data publikacji..."
                alt-format="j F Y, H:i"
                date-format="Z"
                time
              />
            </div>
            <div class="field">
              <input
                id="dateHiddenSwitch"
                type="checkbox"
                class="switch"
                :value="dateHidden"
                :checked="dateHidden"
                @change="toggleDateHidden"
              />
              <label class="label" for="dateHiddenSwitch"
                >Ukryta data publikacji</label
              >
            </div>
            <div class="field">
              <input
                id="favoredSwitch"
                type="checkbox"
                class="switch"
                :value="favored"
                :checked="favored"
                @change="toggleFavored"
              />
              <label class="label" for="favoredSwitch"
                >Wyróżnione ogłoszenie</label
              >
            </div>
          </section>

          <footer class="modal-card-foot">
            <button
              class="button is-success"
              type="submit"
              :class="{ 'is-loading': submitting }"
              :disabled="submitting"
            >
              <icon :name="message ? 'fa-save' : 'fa-plus'" />
              <span>{{ message ? "Zapisz" : "Opublikuj" }}</span>
            </button>
            <button type="button" class="button" @click="cancel">Anuluj</button>
          </footer>
        </div>
      </modal>
    </form>
  </validation-observer>
</template>

<script>
import dayjs from "dayjs";

import { Icon, ControlInput } from "%helpers";
import ControlEditor from "%admin/components/global/ControlEditor";
import ControlDateTimePicker from "%admin/components/global/ControlDateTimePicker";

import WithServerValidation from "%mixins/WithServerValidation";

import { Modal } from "%modals";

export default {
  name: "message-modal",

  components: {
    Modal,
    Icon,
    ControlEditor,
    ControlInput,
    ControlDateTimePicker
  },

  mixins: [
    WithServerValidation({
      "date:PAST_OR_PRESENT": "Data publikacji powinna być datą przeszłą"
    })
  ],

  props: {
    onSubmit: Function,
    message: Object
  },

  data() {
    return {
      title: "",
      content: "",
      date: dayjs().toISOString(),
      dateHidden: false,
      favored: false,
      submitting: false
    };
  },

  mounted() {
    if (this.message) {
      this.title = this.message.title;
      this.content = this.message.content;
      this.date = dayjs(this.message.date).toISOString();
      this.dateHidden = this.message.dateHidden;
      this.favored = this.message.favored;
    }
  },

  methods: {
    submit() {
      this.submitting = true;
      this.onSubmit &&
        this.onSubmit({
          title: this.title,
          content: this.content,
          date: this.date,
          dateHidden: this.dateHidden,
          favored: this.favored
        })
          .then(() => this.cancel())
          .catch(this.handleValidationError)
          .finally(() => (this.submitting = false));
    },

    cancel() {
      this.$emit("hide");
    },

    toggleFavored() {
      this.favored = !this.favored;
    },

    toggleDateHidden() {
      this.dateHidden = !this.dateHidden;
    },

    isDateAvailable(date) {
      return dayjs().isAfter(date);
    },

    // Disclaimer:
    // There is some kind of strange bug that scrolls bottom a modal-card
    // on pasting a really long snippet of text.
    // This is workaround that resets scroll to top after some unnoticeable time (like 10ms).
    // The undersired side effect of this workaround is a single 'blinking' of modal
    // and scrolling background to the bottom.
    // This method is invoked on all change of the form values,
    // like: `title`, `content`, `favored` and `dateHidden` (see 'watch' section).
    fixScrollingBug() {
      this.$refs.modal.scroll(0, 0);
    }
  },

  watch: {
    title() {
      this.fixScrollingBug();
    },

    content() {
      this.fixScrollingBug();
    },

    favored() {
      this.fixScrollingBug();
    },

    date() {
      this.fixScrollingBug();
    },

    dateHidden() {
      this.fixScrollingBug();
    }
  }
};
</script>

<style lang="scss" scoped>
.editor {
  min-height: 10rem;
}

.modal-card {
  @include tablet {
    width: 100%;
  }

  @include desktop {
    width: $desktop;
  }

  &-body {
    // Fix weird behavior that consumes bottom margin of last element
    // of the form on really long modal card body content.
    div:last-child {
      margin-bottom: 1rem;
    }
  }
}
</style>
